import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, User, CheckCircle, AlertCircle, X } from 'lucide-react';
import API_BASE_URL from '../Config';
import { useAuth } from '../contexts/AuthContext';

import { useLayout } from '../contexts/LayoutContext';

export default function SocioSearch({ onSocioSelect, compact = false, showHeaderLabel = true }) {
  const [searchType, setSearchType] = useState('ci'); // 'ci', 'socio', 'nombre'
  const [searchValue, setSearchValue] = useState('');

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [errorMSG, setErrorMSG] = useState('');

  const navigate = useNavigate();
  const { token, handleAuthError } = useAuth();
  const { isCompact } = useLayout();

  const effectiveCompact = compact || isCompact;

  // Debounce helper
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const searchSocios = async (term) => {
    if (!term || term.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMSG('');
    try {
      // Use specific parameters based on searchType for better backend targeting
      let url = `${API_BASE_URL}/socio/buscar?`;
      const cleanTerm = term.replace(/\./g, '').trim(); // Remove dots for numeric/CI searches

      if (searchType === 'ci') {
        url += `documento=${encodeURIComponent(cleanTerm)}`;
      } else {
        url += `q=${encodeURIComponent(term)}`;
      }

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const handledByAuth = handleAuthError(response);
        if (handledByAuth) {
          setLoading(false);
          return;
        }
        throw new Error('Error buscando socios');
      }

      const data = await response.json();
      setResults(data);

      // Auto-select if exact match found logic could go here, but with generic search it's risky.
      // Keeping it simple: user selects.
    } catch (error) {
      console.error('Error:', error);
      setResults([]);
      // Only set generic error if it wasn't a handled auth error (which already shows a toast)
      setErrorMSG('Ocurrió un error al buscar. Verifique su conexión o permisos.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(searchSocios, 400), [token]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setSelectedSocio(null); // Clear selection when typing
    debouncedSearch(value);
  };

  const handleSelectSocio = (socio) => {
    setSelectedSocio(socio);
    setResults([]); // Hide dropdown
    setSearchValue(''); // Clear input for clarity or keep it? 
    // User flow: Select -> Confirm. 
    // If we clear input, we lose context of what was searched.
    // But for visual feedback, we show the "Selected Socio" card.

    // Let's clear search to make it clean, the "Selected Card" will show below (in non-compact)
    // OR in compact mode, we might just fire the callback immediately?

    // If compact mode (which usually implies instant selection implies action), 
    // we might want to trigger `onSocioSelect` immediately or wait for click.
    // The previous code waited for "Confirm".
    // Let's stick to "Click to Select" -> "Review" -> "Confirm" flow for standard,
    // but for `compact` usually we just want to select and show details.

    if (effectiveCompact) {
      if (onSocioSelect) onSocioSelect(socio);
    }
    // In standard mode, we keep local state `selectedSocio` so they can click "Confirmar".
  };

  const handleAccept = () => {
    if (!selectedSocio) return;
    if (onSocioSelect) {
      onSocioSelect(selectedSocio);
    } else {
      navigate(`/socios/detalle/${selectedSocio.id}`);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setResults([]);
    setSelectedSocio(null);
    setErrorMSG('');
  };

  // -- Renders --

  // COMPACT MODE RENDER
  if (effectiveCompact) {
    return (
      <div className="w-full bg-transparent p-0">
        <div className="w-full flex items-center gap-0">
          {showHeaderLabel && <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest hidden md:block px-4">Buscador</h5>}

          {/* Compact Search Bar */}
          <div className="flex-1 flex gap-0 relative z-50">
            <select
              className="bg-slate-50 border-r border-slate-200 text-slate-700 text-[11px] font-black uppercase p-2.5 w-32 outline-none"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="ci">C.I.</option>
              <option value="socio">Socio</option>
              <option value="nombre">Nombre</option>
            </select>

            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-slate-400" />
              </div>
              <input
                type="text"
                className="bg-white text-slate-900 text-[11px] font-bold block w-full pl-10 p-2.5 outline-none placeholder:text-slate-300 focus:bg-slate-50 transition-colors"
                placeholder={searchType === 'ci' ? 'Nro de Cédula (ej: 1234567)...' : searchType === 'socio' ? 'Nro de Socio...' : 'Nombre del socio...'}
                value={searchValue}
                onChange={handleSearchInput}
              />
              {loading && (
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                  <Loader2 className="w-5 h-5 animate-spin text-coop-600" />
                </div>
              )}

              {/* Compact Results Dropdown */}
              {!loading && searchValue.length > 2 && results.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 z-[60] text-center">
                  <p className="text-[10px] font-bold text-slate-400 uppercase italic">No se encontraron socios</p>
                </div>
              )}

              {results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 max-h-96 overflow-y-auto z-[60]">
                  <ul className="divide-y divide-gray-100">
                    {results.map((socio) => (
                      <li
                        key={socio.id}
                        onClick={() => handleSelectSocio(socio)}
                        className="px-6 py-4 hover:bg-slate-50 cursor-pointer flex items-center gap-4 transition-colors group"
                      >
                        <div className="bg-slate-100 p-2 rounded-xl text-slate-500 group-hover:bg-coop-100 group-hover:text-coop-600 transition-all duration-300">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-[12px] font-black text-slate-700 uppercase group-hover:text-coop-800 transition-colors">
                            {socio.personaFisica
                              ? `${socio.personaFisica.nombres} ${socio.personaFisica.apellidos}`
                              : socio.personaJuridica?.razonSocial}
                          </p>
                          <p className="text-[10px] font-bold text-slate-400 font-mono italic">
                            #{socio.id} • {socio.personaFisica ? (socio.personaFisica.cedula || socio.personaFisica.numeroDocumento) : socio.personaJuridica?.ruc}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STANDARD / FULL MODE RENDER
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh]">
      <div className="w-full max-w-5xl enterprise-card">
        {/* Header */}
        <div className="bg-white px-8 py-8 border-b border-enterprise-border text-left">
          <h5 className="text-2xl font-extrabold tracking-tight text-slate-900">Gestión de Socios</h5>
          <p className="text-enterprise-muted text-sm mt-1">Busque y seleccione un socio para gestionar su información y operaciones.</p>
        </div>

        <div className="p-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Select Filter */}
            <div className="md:w-1/4 text-left">
              <select
                className="w-full bg-slate-50 border border-enterprise-border text-slate-900 text-sm rounded-enterprise focus:ring-2 focus:ring-coop-600 focus:border-coop-600 block p-3 outline-none transition-all"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="ci">Cédula de Identidad</option>
                <option value="socio">Código de Socio</option>
                <option value="nombre">Nombre o Razón Social</option>
              </select>
            </div>

            {/* Search Input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-slate-400" />
              </div>
              <input
                type="text"
                className="block w-full p-3 pl-11 text-sm text-slate-900 border border-enterprise-border rounded-enterprise bg-slate-50 focus:ring-2 focus:ring-coop-600 focus:border-coop-600 transition-all outline-none placeholder:text-slate-400"
                placeholder="Ingrese valor para buscar..."
                value={searchValue}
                onChange={handleSearchInput}
              />
              {searchValue && (
                <button
                  onClick={handleClear}
                  className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Search Button */}
            <div className="md:w-32">
              <button
                onClick={() => debouncedSearch(searchValue)}
                disabled={loading}
                className="enterprise-button-primary w-full h-full flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {errorMSG && (
            <div className="mt-6 p-4 bg-rose-50 text-rose-700 rounded-enterprise flex items-center gap-3 text-sm border border-rose-100 italic">
              <AlertCircle className="w-4 h-4" />
              {errorMSG}
            </div>
          )}

          {/* Results List */}
          {!selectedSocio && results.length > 0 && (
            <div className="mt-8 border border-enterprise-border rounded-enterprise overflow-hidden shadow-sm bg-white text-left">
              <div className="bg-slate-50 px-4 py-2 border-b border-enterprise-border">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Resultados Encontrados ({results.length})</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {results.map((socio) => (
                  <li
                    key={socio.id}
                    onClick={() => handleSelectSocio(socio)}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-slate-100 p-2.5 rounded-enterprise text-slate-400 group-hover:bg-coop-50 group-hover:text-coop-600 transition-colors">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900 group-hover:text-coop-700 transition-colors">
                          {socio.personaFisica
                            ? `${socio.personaFisica.nombres} ${socio.personaFisica.apellidos}`
                            : socio.personaJuridica?.razonSocial}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 font-medium">
                          ID: <span className="text-slate-700">{socio.id}</span> • 
                          Doc: <span className="text-slate-700">{(socio.personaFisica?.cedula || socio.personaFisica?.numeroDocumento) || socio.personaJuridica?.ruc}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                       {socio.calificacionRiesgo && (
                         <span className={`enterprise-badge border ${['A', 'A+', 'A-'].includes(socio.calificacionRiesgo.codigo) ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                           ['B', 'B+', 'B-'].includes(socio.calificacionRiesgo.codigo) ? 'bg-amber-50 text-amber-700 border-amber-100' :
                             'bg-rose-50 text-rose-700 border-rose-100'
                           }`}>
                           {socio.calificacionRiesgo.codigo}
                         </span>
                       )}
                       <CheckCircle className="w-5 h-5 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Selected Selection Card */}
          {selectedSocio && (
            <div className="mt-8 bg-slate-50 border border-enterprise-border rounded-enterprise p-6 flex flex-col md:flex-row items-center justify-between gap-6 animate-premium-in shadow-inner text-left">
              <div className="flex items-center gap-5">
                <div className="bg-white p-3 rounded-enterprise border border-enterprise-border shadow-soft">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-slate-900">
                    {selectedSocio.personaFisica
                      ? `${selectedSocio.personaFisica.nombres} ${selectedSocio.personaFisica.apellidos}`
                      : selectedSocio.personaJuridica?.razonSocial}
                  </h4>
                  <p className="text-sm text-enterprise-muted font-medium">
                    Socio Seleccionado • ID <span className="text-slate-900 font-bold">#{selectedSocio.id}</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-3 w-full md:w-auto">
                <button
                  onClick={() => setSelectedSocio(null)}
                  className="flex-1 md:flex-none px-5 py-2.5 border border-enterprise-border text-slate-600 font-bold text-sm rounded-enterprise hover:bg-white transition-all shadow-sm"
                >
                  Cambiar
                </button>
                <button
                  onClick={handleAccept}
                  className="enterprise-button-primary flex-1 md:flex-none px-6 py-2.5 shadow-sm"
                >
                  Acceder a la Ficha
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
